// Removed unused import: AxiosResponse

import { getMenuJobStatus, MenuJobStatusResponse, MenuJobStatus } from '../../services/api';



/**
 * Poll the menu job status until completion or failure
 * @param jobId The ID of the job to poll
 * @param getStatus Function to get the job status
 * @param options Polling options
 * @returns The final job status
 */
export const pollMenuJobStatus = async (
  jobId: string,
  getStatus: (jobId: string) => Promise<MenuJobStatusResponse>,
  options: {
    pollingInterval?: number; // in milliseconds, default 2000ms (2s)
    timeout?: number; // in milliseconds, default 60000ms (60s)
    onProgress?: (status: MenuJobStatusResponse) => void;
    onComplete?: (status: MenuJobStatusResponse) => void;
    onError?: (error: any) => void;
  } = {}
): Promise<MenuJobStatusResponse> => {
  const { pollingInterval = 2000, timeout = 60000, onProgress, onComplete, onError } = options;
  const startTime = Date.now();
  let pollCount = 0;
  const maxPolls = Math.ceil(timeout / pollingInterval) + 100; // Failsafe

  // Create a promise that resolves when the job completes or fails
  return new Promise((resolve, reject) => {
    const checkStatus = async () => {
      try {
        pollCount++;
        const status = await getStatus(jobId);
        // Log every poll for debugging
        // eslint-disable-next-line no-console
        console.log(`[pollMenuJobStatus] Poll #${pollCount} jobId=${jobId} status=`, status.status, status);
        if (onProgress) onProgress(status);

        // If job is complete or failed, resolve the promise
        if (status.status === MenuJobStatus.COMPLETED || status.status === MenuJobStatus.FAILED) {
          if (onComplete) onComplete(status);
          resolve(status);
          return;
        }

        // If status is not recognized, log and break to avoid infinite loop
        const validStatuses = [
          MenuJobStatus.QUEUED,
          MenuJobStatus.STARTED,
          MenuJobStatus.IN_PROGRESS,
          MenuJobStatus.COMPLETED,
          MenuJobStatus.FAILED
        ];
        if (!validStatuses.includes(status.status as MenuJobStatus)) {
          const err = new Error(`Unknown menu job status: ${status.status}`);
          console.error(`[pollMenuJobStatus] Unknown status for jobId=${jobId}:`, status);
          if (onError) onError(err);
          reject(err);
          return;
        }

        // If timeout exceeded, reject
        if (Date.now() - startTime > timeout) {
          const err = new Error('Polling menu job timed out');
          if (onError) onError(err);
          reject(err);
          return;
        }

        // Failsafe: if too many polls, reject
        if (pollCount > maxPolls) {
          const err = new Error('Polling menu job exceeded max allowed polls');
          if (onError) onError(err);
          reject(err);
          return;
        }

        // Only recurse asynchronously
        setTimeout(checkStatus, pollingInterval);
      } catch (error) {
        console.error('Error polling menu job status:', error);
        if (onError) onError(error);
        reject(error);
      }
    };
    // Start polling
    checkStatus();
  });
};

/**
 * Hook to poll menu publish job status
 */
export const useMenuJobPolling = () => {
  /**
   * Poll the status of a menu publish job
   * @param jobId The ID of the job to poll
   * @returns The final job status
   */
  const pollJobStatus = async (jobId: string): Promise<MenuJobStatusResponse> => {
    return pollMenuJobStatus(jobId, getMenuJobStatus, {
      pollingInterval: 2000, // Poll every 2 seconds
      // timeout: 60000, // Timeout after 60 seconds
      onProgress: (jobStatus) => {
        console.log('Menu job progress:', jobStatus);
      },
      onComplete: (jobStatus) => {
        console.log(`Menu job ${jobId} completed with status: ${jobStatus.status}`);
      },
      onError: (error) => {
        console.error(`Error polling menu job ${jobId}:`, error);
      },
    });
  };

  return {
    pollJobStatus,
  };
};
